import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import { TimePicker } from 'antd';
import { useEffect, useState, useMemo, useRef } from 'react';
import Image from 'global/Image';
import { useRunningTradeStore } from 'global/Datafeed/store/runningTrade';
import moment, { Moment } from 'moment';
import { actionTypeMenus, predefinedLot } from './constants';
import { ButtonDropdownTrigger, DropdownWrapper } from '../styled';
import { useFilterStore } from '../../store';
import { RunningTradeCustomFilterActionEnum } from '../../types';
import { Button, ButtonChip, FormWrapper, FormFooterWrapper, TextErrorMessage, Dot, NumberInput, TimePickerInputContainer, TimePickerPopoverContainer } from './styled';
import { formatterThousand, onPasteNumbersOnly, parserThousand, validateDecimalNumber, validateNumbersOnly } from './utils';
import { isNumber } from '../../utils';

interface AdvancedFilterProps {
  isMarketOpen: boolean;
  timeFormat?: string;
}

const AdvancedFilter = ({
  isMarketOpen,
  timeFormat = 'HH:mm',
}: AdvancedFilterProps) => {
  const popoverRef = useRef(null);
  const popoverButtonRef = useRef(null);

  const filter = useFilterStore((s) => s.filter);

  const onApplyFilter = useFilterStore((s) => s.onApplyFilter);
  const onResetFilter = useFilterStore((s) => s.onResetFilter);

  const clearData = useRunningTradeStore((state) => state.clearData);

  const [visible, setVisible] = useState(false);

  const [actionType, setActionType] =
    useState<RunningTradeCustomFilterActionEnum>(RunningTradeCustomFilterActionEnum.ALL);
  const [minPrice, setMinPrice] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [minPercentage, setMinPercentage] = useState<number | null>(null);
  const [maxPercentage, setMaxPercentage] = useState<number | null>(null);
  const [minLot, setMinLot] = useState<number | null>(null);
  const [timeRangeStart, setTimeRangeStart] = useState<Moment>(null);
  const [timeRangeEnd, setTimeRangeEnd] = useState<Moment>(null);

  const handleClickOutside = (event) => {
    event.preventDefault();
    if (popoverButtonRef.current?.contains(event.target)) return;
    if (popoverRef.current && !popoverRef.current.contains(event.target)) {
      setVisible(false);
    }
  };

  useEffect(() => {
    if (visible) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [visible]);

  const handleResetForm = () => {
    setActionType(RunningTradeCustomFilterActionEnum.ALL);
    setMinPrice(null);
    setMaxPrice(null);
    setMinPercentage(null);
    setMaxPercentage(null);
    setMinLot(null);
    setTimeRangeStart(null);
    setTimeRangeEnd(null);
    clearData('widget');
    onResetFilter();
  };

  const handleApplyFilter = () => {
    setVisible(false);
    clearData('widget');
    onApplyFilter({
      minPrice,
      maxPrice,
      minPercentage,
      maxPercentage,
      minLot,
      actionType,
      timeRangeStart: timeRangeStart ? timeRangeStart.format(timeFormat).toString() : null,
      timeRangeEnd: timeRangeEnd ? timeRangeEnd.format(timeFormat).toString() : null,
    });
  };

  const handleClickButton = () => {
    const isVisible = !visible;
    setVisible(isVisible);

    if (isVisible) {
      setActionType(filter?.actionType || RunningTradeCustomFilterActionEnum.ALL);
      setMinPrice(isNumber(filter?.minPrice) ? filter?.minPrice : null);
      setMaxPrice(isNumber(filter?.maxPrice) ? filter?.maxPrice : null);
      setMinPercentage(isNumber(filter?.minPercentage) ? filter?.minPercentage : null);
      setMaxPercentage(isNumber(filter?.maxPercentage) ? filter?.maxPercentage : null);
      setMinLot(isNumber(filter?.minLot) ? filter?.minLot : null);
    }
  };

  const onPasteNumberValidate = (e) => {
    const clipboardData = e.clipboardData || window.Clipboard;
    const pastedData = clipboardData.getData('Text').toUpperCase();

    if (pastedData.indexOf('E') > -1) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const priceRangeValidation = useMemo(() => {
    if (!isNumber(maxPrice) || !isNumber(minPrice)) return null;
    if (maxPrice < minPrice) {
      return {
        message: 'Maximum price must be greater than the minimum price',
        status: 'error',
      };
    }
    return null;
  }, [minPrice, maxPrice]);

  const percentageValidation = useMemo(() => {
    if (!isNumber(maxPercentage) || !isNumber(minPercentage)) return null;
    if (maxPercentage < minPercentage) {
      return {
        message: 'Maximum price change must be greater than the minimum price change',
        status: 'error',
      };
    }
    return null;
  }, [minPercentage, maxPercentage]);

  const timeRangeValidation = useMemo(() => {
    if (isMarketOpen) return null;
    if (timeRangeStart && timeRangeEnd) {
      const isNotValid = moment(timeRangeEnd, 'HH:mm').isBefore(moment(timeRangeStart, 'HH:mm'));
      if (isNotValid) {
        return {
          message: 'The end time must be later than the start time.',
          status: 'error',
        };
      }
    }
    return null;
  }, [isMarketOpen, timeRangeStart, timeRangeEnd]);

  const isButtonApplyDisabled = useMemo(() => {
    if (priceRangeValidation || percentageValidation || timeRangeValidation) {
      return true;
    }
    return false;
  }, [priceRangeValidation, percentageValidation, timeRangeValidation]);

  const isFilterApplied = useMemo(() => {
    const arrayFilterType = [
      typeof filter?.minPrice,
      typeof filter?.maxPrice,
      typeof filter?.minPercentage,
      typeof filter?.maxPercentage,
      typeof filter?.minLot,
    ];
    if (!filter) return false;
    if (
      arrayFilterType.includes('number') ||
      filter?.actionType !== RunningTradeCustomFilterActionEnum.ALL
    ) {
      return true;
    }
    if (filter?.timeRangeStart || filter.timeRangeEnd) {
      return true;
    }
    return false;
  }, [filter]);

  return (
    <div>
      <ButtonDropdownTrigger
        ref={popoverButtonRef}
        onClick={handleClickButton}
        isOpened={visible}
        style={{ padding: '6px 10px' }}
      >
        <Image src="/icon/rt-filter.svg" />
        <Text size="12px">Filter</Text>
        {isFilterApplied && <Dot />}
      </ButtonDropdownTrigger>
      {visible && (
        <DropdownWrapper ref={popoverRef}>
          <FormWrapper>
            <div>
              {/* @ts-expect-error */}
              <Text weight={700} mb="6px">Action Type</Text>
              <Flex style={{ gap: 12 }}>
                {actionTypeMenus.map((item) => (
                  <ButtonChip
                    key={item.key}
                    isActive={actionType === item.key}
                    onClick={() => setActionType(item.key)}
                  >
                    <Text size="12px">{item.label}</Text>
                  </ButtonChip>
                ))}
              </Flex>
            </div>
            <div>
              {/* @ts-expect-error */}
              <Text weight={700} mb="6px">Price Range (Rp)</Text>
              {/* @ts-expect-error */}
              <Flex style={{ gap: 16 }} mb="6px">
                <NumberInput
                  onChange={(value) => setMinPrice(Number(value))}
                  value={minPrice}
                  placeholder="From"
                  // @ts-expect-error
                  status={priceRangeValidation?.status}
                  min={0}
                  max={999999}
                  maxlength={7}
                  onKeyDown={validateNumbersOnly}
                  onPaste={onPasteNumbersOnly}
                  formatter={formatterThousand}
                  parser={parserThousand}
                />
                <NumberInput
                  onChange={(value) => setMaxPrice(Number(value))}
                  value={maxPrice}
                  placeholder="To"
                  min={0}
                  max={999999}
                  // @ts-expect-error
                  maxlength={7}
                  onKeyDown={validateNumbersOnly}
                  onPaste={onPasteNumbersOnly}
                  formatter={formatterThousand}
                  parser={parserThousand}
                />
              </Flex>
              {priceRangeValidation && (
                <TextErrorMessage>
                  {priceRangeValidation.message}
                </TextErrorMessage>
              )}
            </div>
            {isMarketOpen && (
              <div>
                {/* @ts-expect-error */}
                <Text weight={700} mb="6px">Price Change (%)</Text>
                {/* @ts-expect-error */}
                <Flex style={{ gap: 16 }} mb="6px">
                  <NumberInput
                    onChange={(value) => setMinPercentage(Number(value))}
                    value={minPercentage}
                    placeholder="From"
                    // @ts-expect-error
                    status={percentageValidation?.status}
                    min={-35}
                    max={35}
                    maxlength={6}
                    onKeyDown={validateDecimalNumber}
                    onPaste={onPasteNumbersOnly}
                  />
                  <NumberInput
                    onChange={(value) => setMaxPercentage(Number(value))}
                    value={maxPercentage}
                    placeholder="To"
                    min={-35}
                    max={35}
                    // @ts-expect-error
                    maxlength={6}
                    onKeyDown={validateDecimalNumber}
                    onPaste={onPasteNumbersOnly}
                  />
                </Flex>
                {percentageValidation && (
                  <TextErrorMessage>
                    {percentageValidation.message}
                  </TextErrorMessage>
                )}
              </div>
            )}
            <div>
              {/* @ts-expect-error */}
              <Text weight={700} mb="6px">Minimum Lot</Text>
              {/* @ts-expect-error */}
              <Flex mb="6px">
                <NumberInput
                  onChange={(value) => setMinLot(Number(value))}
                  value={minLot}
                  placeholder="e.g 1000"
                  onKeyDown={validateNumbersOnly}
                  onPaste={onPasteNumberValidate}
                  formatter={formatterThousand}
                  parser={parserThousand}
                  max={50000}
                />
              </Flex>
              <Flex style={{ gap: 8 }}>
                {predefinedLot.map((item) => (
                  <ButtonChip
                    key={item.value}
                    isActive={item.value === minLot}
                    onClick={() => setMinLot(item.value)}
                  >
                    <Text size="12px">{item.label}</Text>
                  </ButtonChip>
                ))}
              </Flex>
            </div>
            {!isMarketOpen && (
              <div>
                {/* @ts-expect-error */}
                <Text weight={700} mb="6px">Time Range</Text>
                <TimePickerPopoverContainer id="rt-advanced-time-picker" />
                <TimePickerInputContainer>
                  <TimePicker
                    defaultValue={timeRangeStart}
                    format={timeFormat}
                    getPopupContainer={() => document.getElementById('rt-advanced-time-picker')}
                    suffixIcon={null}
                    placeholder="from"
                    onChange={(value) => setTimeRangeStart(value)}
                    value={timeRangeStart}
                  />
                  <TimePicker
                    defaultValue={timeRangeEnd}
                    format={timeFormat}
                    getPopupContainer={() => document.getElementById('rt-advanced-time-picker')}
                    suffixIcon={null}
                    placeholder="to"
                    onChange={(value) => setTimeRangeEnd(value)}
                    value={timeRangeEnd}
                  />
                </TimePickerInputContainer>
                {timeRangeValidation && (
                  <TextErrorMessage>
                    {timeRangeValidation.message}
                  </TextErrorMessage>
                )}
              </div>
            )}
          </FormWrapper>
          <FormFooterWrapper>
            <Button
              onClick={handleResetForm}
              style={{ color: '#858585' }}
            >
              Reset
            </Button>
            <Button
              onClick={handleApplyFilter}
              type="primary"
              disabled={isButtonApplyDisabled}
            >
              Apply
            </Button>
          </FormFooterWrapper>
        </DropdownWrapper>
      )}
    </div>
  );
};

export default AdvancedFilter;
